/**
 * Date helpers — Europe/Moscow canonical (Q29 / T5.2).
 *
 * The server is the date authority (T5.3); the FE only formats and does
 * cosmetic comparisons. Where the FE *does* need a "today" anchor (e.g.
 * client-side disabled-state of a date picker before the first server
 * roundtrip), it should use `today()` here so the answer always lands in
 * MSK regardless of the user's local timezone.
 *
 * date-fns-tz handles DST boundary transitions correctly: the function
 * `formatInTimeZone` treats the date as a UTC instant and renders it with
 * MSK rules. Russia is on a fixed UTC+3 offset since 2014 (no DST), so the
 * boundary case is a defensive test rather than a real concern, but is
 * kept so a future locale change is caught.
 */
import { format as dfFormat, parse as dfParse, parseISO, startOfDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

export const TIMEZONE = 'Europe/Moscow';

/**
 * Returns the UTC `Date` corresponding to today's start-of-day in MSK.
 * Comparisons against this anchor (`isPast`/`isFuture`/`isToday`) treat the
 * day as a calendar bucket, not a clock instant.
 */
export function today(): Date {
  const nowMsk = toZonedTime(new Date(), TIMEZONE);
  const startMsk = startOfDay(nowMsk);
  return fromZonedTime(startMsk, TIMEZONE);
}

/** Coerce common inputs to a Date. ISO strings + Dates only — no `any`. */
function asDate(d: Date | string): Date {
  return typeof d === 'string' ? parseISO(d) : d;
}

/** True if `d` falls on an MSK calendar day before today (MSK). */
export function isPast(d: Date | string, anchor: Date = today()): boolean {
  const dayKey = formatInTimeZone(asDate(d), TIMEZONE, 'yyyy-MM-dd');
  const anchorKey = formatInTimeZone(anchor, TIMEZONE, 'yyyy-MM-dd');
  return dayKey < anchorKey;
}

/** True if `d` falls on an MSK calendar day after today (MSK). */
export function isFuture(d: Date | string, anchor: Date = today()): boolean {
  const dayKey = formatInTimeZone(asDate(d), TIMEZONE, 'yyyy-MM-dd');
  const anchorKey = formatInTimeZone(anchor, TIMEZONE, 'yyyy-MM-dd');
  return dayKey > anchorKey;
}

/** True if `d` is the same MSK calendar day as today (MSK). */
export function isToday(d: Date | string, anchor: Date = today()): boolean {
  const dayKey = formatInTimeZone(asDate(d), TIMEZONE, 'yyyy-MM-dd');
  const anchorKey = formatInTimeZone(anchor, TIMEZONE, 'yyyy-MM-dd');
  return dayKey === anchorKey;
}

/**
 * Format a date for display in RU locale, MSK-anchored.
 * Default `'PPP'` → "23 апреля 2026 г." (long localized).
 * Other useful tokens: `'dd.MM.yyyy'`, `'dd.MM.yyyy HH:mm'`, `'d MMM yyyy'`.
 */
export function formatDate(d: Date | string, formatStr: string = 'PPP'): string {
  return formatInTimeZone(asDate(d), TIMEZONE, formatStr, { locale: ru });
}

/**
 * Parse a user-entered string assumed to be in MSK with an explicit format
 * (e.g. `'dd.MM.yyyy'`). Returns the corresponding UTC `Date`.
 *
 * For ISO-8601 strings, prefer `parseISO` directly — the timezone is then
 * carried by the input.
 */
export function parseDate(input: string, formatStr: string = 'dd.MM.yyyy'): Date {
  const naive = dfParse(input, formatStr, new Date(), { locale: ru });
  // Treat `naive` as if its wall-clock components are MSK-local, then map
  // back to UTC. This is the inverse of `toZonedTime`.
  return fromZonedTime(naive, TIMEZONE);
}

// Re-exports for convenience (avoid leaking date-fns import paths into pages).
export { dfFormat, parseISO };

// ─── Phase-5 W3.T3.4 ─────────────────────────────────────────────────────

const RU_MONTH_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
] as const;

const RU_WEEKDAY_NOMINATIVE_LOWER = [
  'воскресенье',
  'понедельник',
  'вторник',
  'среда',
  'четверг',
  'пятница',
  'суббота',
] as const;

/**
 * Format an ISO week range for the dashboard header («20–26 апреля 2026»).
 *
 * Phase 5 W3.T3.4 / round-2 / UI S-7.
 *
 * Rules:
 *   - Same month, same year → «20–26 апреля 2026».
 *   - Different month, same year → «28 апр – 04 мая 2026» (short form to
 *     keep the header narrow on mobile).
 *   - Same calendar day (single-day edge case, e.g. Monday-only window)
 *     → «понедельник 04 мая 2026».
 *
 * @param weekFrom YYYY-MM-DD (Monday of the ISO week)
 * @param weekTo   YYYY-MM-DD (Sunday of the ISO week, or same as `weekFrom`
 *                 for the single-day edge case)
 */
export function formatIsoWeekRangeRu(weekFrom: string, weekTo: string): string {
  const [fy, fm, fd] = parseDateParts(weekFrom);
  const [ty, tm, td] = parseDateParts(weekTo);

  if (fy === ty && fm === tm && fd === td) {
    // Single-day edge case — derive weekday name from a UTC anchor (Russia
    // is fixed UTC+3, so reading getUTCDay on a midnight-Z date yields the
    // intended local weekday for any date the BE could send).
    const anchor = new Date(`${weekFrom}T00:00:00Z`);
    const wd = RU_WEEKDAY_NOMINATIVE_LOWER[anchor.getUTCDay()] ?? '';
    return `${wd} ${pad2(fd)} ${RU_MONTH_GENITIVE[fm - 1]} ${fy}`;
  }

  if (fy === ty && fm === tm) {
    // Same month: «20–26 апреля 2026»
    return `${pad2(fd)}–${pad2(td)} ${RU_MONTH_GENITIVE[fm - 1]} ${ty}`;
  }

  // Cross-month: short genitive form so the line stays compact.
  const fromShort = `${pad2(fd)} ${shortRuMonth(fm)}`;
  const toShort = `${pad2(td)} ${shortRuMonth(tm)}`;
  return `${fromShort} – ${toShort} ${ty}`;
}

/**
 * Календарная дата `YYYY-MM-DD` человеку: `20.08.2026`.
 *
 * ⚠️ ЖИВЁТ ЗДЕСЬ, А НЕ В КОМПОНЕНТЕ. Правка П-9 круга ревью: машинный вид даты
 * (`2026-08-20`) стоял в дереве разделов, в окне назначения и в карточке
 * работы, тогда как «Обзор» той же карточки печатал человеческий. Одна дата в
 * двух видах на соседних вкладках читается как две разные величины; соседняя
 * волна получила замечание ровно за это.
 *
 * Без `Date` и часовых поясов: строка режется посимвольно. `new Date(iso)`
 * трактует `YYYY-MM-DD` как UTC-полночь, и в поясе восточнее Гринвича вечерняя
 * дата уехала бы на сутки — ровно та ошибка, от которой уводит серверное
 * «сегодня» в контракте команды.
 *
 * Негодный вход возвращается КАК ЕСТЬ, а не превращается в «—»: подставленный
 * прочерк спрятал бы поломку данных, а показанная как есть строка её выдаёт.
 */
export function formatIsoDateRu(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso ?? '';
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

function parseDateParts(iso: string): [number, number, number] {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    throw new Error(`formatIsoWeekRangeRu: invalid ISO date "${iso}"`);
  }
  return [y, m, d];
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function shortRuMonth(m: number): string {
  // Genitive short forms used in colloquial RU date ranges; «май» has no
  // shortening so it stays as «мая» (per RU style guides).
  const SHORT = [
    'янв',
    'фев',
    'мар',
    'апр',
    'мая',
    'июн',
    'июл',
    'авг',
    'сен',
    'окт',
    'ноя',
    'дек',
  ] as const;
  return SHORT[m - 1] ?? '';
}
